import React, { useEffect, useState } from "react";
import { UserAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { FaGoogle, FaEnvelope, FaEye, FaEyeSlash } from "react-icons/fa";
import HeaderComp from "./Components/HeaderComp";
import Footer from "./Components/Footer";
import Logo from "../media/LogoHD.png";
import { useLang } from "../i18n/LanguageContext";

const Signin = () => {
  const {
    googleSignIn,
    emailSignUp,
    emailSignIn,
    resetPassword,
    createUserProfileDocument,
    refreshUser,
    logOut,
    user,
    loading: authLoading,
  } = UserAuth();
  const navigate = useNavigate();
  const { t } = useLang();

  const [isRegistering, setIsRegistering] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [role, setRole] = useState("Étudiant");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [parentOk, setParentOk] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  // Connexion / inscription par e-mail
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Compte e-mail existant mais profil pas encore complété
  const [pendingUser, setPendingUser] = useState(null);

  // Âge à partir de la date de naissance (null si la date est invalide)
  const age = (() => {
    const d = new Date(dateOfBirth);
    if (!dateOfBirth || isNaN(d.getTime()) || d >= new Date()) return null;
    const now = new Date();
    let a = now.getFullYear() - d.getFullYear();
    if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) a -= 1;
    return a;
  })();
  const isMinor = age !== null && age < 18;
  const isChild = age !== null && age < 13; // compte créé par le parent

  const isFormValid =
    firstName.trim() &&
    lastName.trim() &&
    age !== null &&
    acceptTerms &&
    (!isMinor || parentOk);

  // Déjà connecté avec un profil complet → direction le compte
  useEffect(() => {
    if (!authLoading && !loading && user?.hasProfile) navigate("/account");
  }, [user, authLoading, loading, navigate]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const passwordValid = password.length >= 8;

  const buildProfile = (firebaseUser) => ({
    FirstName: firstName.trim(),
    LastName: lastName.trim(),
    Role: role === "Étudiant" ? 2 : 1,
    User: firebaseUser,
    DateOfBirth: dateOfBirth,
    ParentalConsent: isMinor
      ? { declaredAt: new Date().toISOString(), by: isChild ? "parent" : "mineur" }
      : null,
  });

  // Messages d'erreur Firebase Auth → texte compréhensible
  const authError = (err) => {
    switch (err?.code) {
      case "auth/popup-closed-by-user":
      case "auth/cancelled-popup-request":
        return t("Connexion annulée.");
      case "auth/popup-blocked":
        return t("La fenêtre de connexion a été bloquée par le navigateur. Autorisez les pop-ups puis réessayez.");
      case "auth/unauthorized-domain":
        return t("Ce domaine n'est pas autorisé dans Firebase (Authentication → Settings → Authorized domains).");
      case "auth/email-already-in-use":
        return t("Un compte existe déjà avec cette adresse e-mail. Connecte-toi (ou utilise « Mot de passe oublié »).");
      case "auth/invalid-email":
        return t("Adresse e-mail invalide.");
      case "auth/weak-password":
        return t("Mot de passe trop faible : au moins 8 caractères.");
      case "auth/invalid-credential":
      case "auth/wrong-password":
      case "auth/user-not-found":
      case "auth/invalid-login-credentials":
        return t("E-mail ou mot de passe incorrect.");
      case "auth/too-many-requests":
        return t("Trop de tentatives. Patiente quelques minutes puis réessaie.");
      case "auth/operation-not-allowed":
        return t("La connexion par e-mail n'est pas encore activée sur le site.");
      case "auth/account-exists-with-different-credential":
        return t("Cette adresse e-mail est déjà liée à une autre méthode de connexion (Google ou mot de passe).");
      case "auth/network-request-failed":
        return t("Pas de connexion Internet. Vérifie ton réseau puis réessaie.");
      default:
        return t("Une erreur est survenue. Veuillez réessayer.");
    }
  };

  /** Inscription par e-mail + mot de passe */
  const handleEmailRegister = async (e) => {
    e?.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      let firebaseUser = pendingUser;
      if (!firebaseUser) {
        const cred = await emailSignUp(email, password, `${firstName.trim()} ${lastName.trim()}`);
        firebaseUser = cred.user;
      }
      await createUserProfileDocument(buildProfile(firebaseUser), false);
      setPendingUser(null);
      await refreshUser();
      navigate("/account");
    } catch (err) {
      console.error(err);
      setError(authError(err));
    } finally {
      setLoading(false);
    }
  };

  /** Connexion par e-mail + mot de passe */
  const handleEmailLogin = async (e) => {
    e?.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const cred = await emailSignIn(email, password);
      const response = await createUserProfileDocument({ User: cred.user }, true);
      if (response === "No user found.") {
        // Compte créé mais profil jamais complété : on le termine ici
        setPendingUser(cred.user);
        setIsRegistering(true);
        setInfo(t("Il manque quelques informations pour terminer ton inscription."));
        return;
      }
      await refreshUser();
      navigate("/account");
    } catch (err) {
      console.error(err);
      setError(authError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError("");
    setInfo("");
    if (!emailValid) {
      setError(t("Écris d'abord ton adresse e-mail ci-dessus."));
      return;
    }
    try {
      await resetPassword(email);
    } catch (err) {
      if (err?.code === "auth/invalid-email") return setError(t("Adresse e-mail invalide."));
      if (err?.code === "auth/too-many-requests") return setError(authError(err));
      // Les autres erreurs ne disent pas si le compte existe (sécurité)
    }
    setInfo(t("Si un compte existe avec cette adresse, un e-mail pour choisir un nouveau mot de passe vient d'être envoyé (pense à regarder dans les spams)."));
  };

  const switchTab = (registering) => {
    setIsRegistering(registering);
    setError("");
    setInfo("");
    if (!registering && pendingUser) {
      setPendingUser(null);
      logOut().catch(() => {});
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      const result = await googleSignIn();
      const response = await createUserProfileDocument(buildProfile(result.user), !isRegistering);
      if (response === "No user found.") {
        // Pas de profil : on déconnecte pour ne pas laisser une session "fantôme"
        await logOut();
        setError(t("Aucun compte trouvé avec ce compte Google. Créez d'abord un compte."));
        setIsRegistering(true);
        return;
      }
      await refreshUser();
      navigate("/account");
    } catch (err) {
      setError(authError(err));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-creme flex flex-col">
      <HeaderComp />

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src={Logo} alt="Tatie Soso" className="h-20 w-auto mx-auto mb-3" />
            <p className="text-stone-500 italic text-sm">{t("Que la magie opère...")}</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Tabs */}
            <div className="flex">
              <button
                className={`flex-1 py-4 text-sm font-semibold transition-all ${
                  !isRegistering
                    ? "bg-prune text-white"
                    : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                }`}
                onClick={() => switchTab(false)}
              >
                {t("Connexion")}
              </button>
              <button
                className={`flex-1 py-4 text-sm font-semibold transition-all ${
                  isRegistering
                    ? "bg-prune text-white"
                    : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                }`}
                onClick={() => switchTab(true)}
              >
                {t("Créer un compte")}
              </button>
            </div>

            <div className="p-8 space-y-5">
              {/* Champs inscription uniquement */}
              {isRegistering && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Prénom")}</label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Marie"
                        className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Nom")}</label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Dupont"
                        className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Date de naissance")}</label>
                    <input
                      type="date"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Je suis")}</label>
                    <select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune bg-white"
                    >
                      <option value="Étudiant">{t("Étudiant")}</option>
                      <option value="Professeur">{t("Professeur")}</option>
                    </select>
                  </div>

                  {/* Mineurs : accord du parent / tuteur légal */}
                  {isMinor && (
                    <label className="flex items-start gap-3 text-sm text-stone-700 bg-or-light border border-or rounded-lg p-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={parentOk}
                        onChange={(e) => setParentOk(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-prune shrink-0"
                      />
                      <span>
                        {isChild
                          ? t("Je suis le parent ou le tuteur légal de cet enfant de moins de 13 ans, je crée ce compte pour lui et j'accepte les conditions en son nom.")
                          : t("J'ai moins de 18 ans et mon parent ou tuteur légal est d'accord pour que je m'inscrive et que j'utilise Tatie Soso.")}
                      </span>
                    </label>
                  )}

                  {/* Acceptation des CGV et de la politique de confidentialité */}
                  <label className="flex items-start gap-3 text-sm text-stone-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-prune shrink-0"
                    />
                    <span>
                      {t("J'accepte les")}{" "}
                      <Link to="/cgv" target="_blank" className="text-prune underline">{t("conditions générales")}</Link>{" "}
                      {t("et j'ai lu la")}{" "}
                      <Link to="/confidentialite" target="_blank" className="text-prune underline">{t("politique de confidentialité")}</Link>.
                    </span>
                  </label>
                </>
              )}

              {info && (
                <div className="bg-sauge-light border border-sauge/30 text-sauge-dark rounded-lg px-4 py-3 text-sm">
                  {info}
                </div>
              )}

              {/* Erreur */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                  ⚠️ {error}
                </div>
              )}

              {/* E-mail + mot de passe (sans Google) */}
              {pendingUser ? (
                <button
                  onClick={handleEmailRegister}
                  disabled={loading || !isFormValid}
                  className={`w-full py-3 rounded-full font-semibold text-sm transition-all ${
                    loading || !isFormValid
                      ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                      : "bg-prune text-white hover:bg-prune-dark"
                  }`}
                >
                  {loading ? t("Chargement...") : t("Terminer mon inscription")}
                </button>
              ) : (
                <form onSubmit={isRegistering ? handleEmailRegister : handleEmailLogin} className="space-y-3">
                  <div>
                    <label htmlFor="signin-email" className="block text-xs font-semibold text-stone-600 mb-1">{t("Adresse e-mail")}</label>
                    <input
                      id="signin-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("exemple@email.com")}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune"
                    />
                  </div>
                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <label htmlFor="signin-password" className="block text-xs font-semibold text-stone-600">{t("Mot de passe")}</label>
                      {!isRegistering && (
                        <button type="button" onClick={handleForgotPassword} className="text-xs text-prune hover:underline">
                          {t("Mot de passe oublié ?")}
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        id="signin-password"
                        type={showPassword ? "text" : "password"}
                        autoComplete={isRegistering ? "new-password" : "current-password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full border border-stone-200 rounded-lg pl-3 pr-12 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? t("Masquer le mot de passe") : t("Afficher le mot de passe")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-prune"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {isRegistering && (
                      <p className={`text-xs mt-1 ${password && !passwordValid ? "text-red-600" : "text-stone-500"}`}>
                        {t("Au moins 8 caractères.")}
                      </p>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !emailValid || (isRegistering ? !passwordValid || !isFormValid : !password)}
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-sm transition-all ${
                      loading || !emailValid || (isRegistering ? !passwordValid || !isFormValid : !password)
                        ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                        : "bg-prune text-white hover:bg-prune-dark"
                    }`}
                  >
                    <FaEnvelope />
                    {loading ? t("Chargement...") : isRegistering ? t("Créer mon compte") : t("Se connecter")}
                  </button>
                </form>
              )}

              {!pendingUser && (
                <>
                  <div className="flex items-center gap-3 text-xs text-stone-400" aria-hidden="true">
                    <span className="flex-1 h-px bg-stone-200" />
                    {t("ou")}
                    <span className="flex-1 h-px bg-stone-200" />
                  </div>

                {/* Bouton Google */}
                <button
                  onClick={handleGoogleSignIn}
                  disabled={loading || (isRegistering && !isFormValid)}
                  className={`w-full flex items-center justify-center gap-3 py-3 rounded-full font-semibold text-sm transition-all ${
                    loading || (isRegistering && !isFormValid)
                      ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                      : "bg-white text-stone-700 border border-stone-300 hover:border-prune hover:text-prune"
                  }`}
                >
                  <FaGoogle />
                  {loading
                    ? t("Chargement...")
                    : isRegistering
                    ? t("Créer mon compte avec Google")
                    : t("Continuer avec Google")}
                </button>
                </>
              )}

              {!isRegistering && (
                <p className="text-center text-xs text-stone-600">
                  {t("En vous connectant, vous restez soumis·e aux")}{" "}
                  <Link to="/cgv" className="text-prune underline">{t("conditions générales")}</Link>{" "}
                  {t("et à la")}{" "}
                  <Link to="/confidentialite" className="text-prune underline">{t("politique de confidentialité")}</Link>.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Signin;
